import { useState, useEffect, useRef } from 'react';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 50);
        rafRef.current = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
      role="navigation"
      aria-label="Main navigation"
      itemScope
      itemType="https://schema.org/SiteNavigationElement"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center">
            <h1 className={`text-xl font-bold transition-colors duration-300 ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}>
              <a 
                href="/" 
                className="hover:opacity-80 transition-opacity"
                aria-label="Passport Visa Photo – Home"
                itemProp="url"
              >
                <span itemProp="name">Passport Visa Photo</span>
              </a>
            </h1>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <a
              href="#services"
              onClick={(e) => handleNavClick(e, 'services')}
              className={`text-sm font-medium transition-colors duration-300 hover:text-blue-900 whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
              itemProp="url"
            >
              <span itemProp="name">Services</span>
            </a>
            <a
              href="#how-it-works"
              onClick={(e) => handleNavClick(e, 'how-it-works')}
              className={`text-sm font-medium transition-colors duration-300 hover:text-blue-900 whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
              itemProp="url"
            >
              <span itemProp="name">How It Works</span>
            </a>
            <a
              href="#faq"
              onClick={(e) => handleNavClick(e, 'faq')}
              className={`text-sm font-medium transition-colors duration-300 hover:text-blue-900 whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
              itemProp="url"
            >
              <span itemProp="name">FAQ</span>
            </a>
            <a
              href="#reviews"
              onClick={(e) => handleNavClick(e, 'reviews')}
              className={`text-sm font-medium transition-colors duration-300 hover:text-blue-900 whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
              itemProp="url"
            >
              <span itemProp="name">Reviews</span>
            </a>
            <a
              href="#contact"
              onClick={(e) => handleNavClick(e, 'contact')}
              className={`text-sm font-medium transition-colors duration-300 hover:text-blue-900 whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700' : 'text-white'
              }`}
              itemProp="url"
            >
              <span itemProp="name">Contact</span>
            </a>
            <a
              href="tel:+16476289141"
              className="bg-blue-900 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
              aria-label="Call Passport Visa Photo at 647-628-9141"
            >
              Call Now
            </a>
          </div>

          <div className="md:hidden flex items-center gap-3">
            <a
              href="tel:+16476289141"
              className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-blue-900' : 'text-white'
              }`}
              aria-label="Call Passport Visa Photo at 647-628-9141"
            >
              <i className="ri-phone-line text-xl"></i>
            </a>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`text-2xl transition-colors duration-300 cursor-pointer ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              <i className={isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-0 top-20 bg-white z-40 transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col p-6 space-y-4">
          <a
            href="#services"
            onClick={(e) => handleNavClick(e, 'services')}
            className="text-base font-medium text-gray-900 hover:text-blue-900 py-3 border-b border-gray-200 cursor-pointer"
            itemProp="url"
          >
            <span itemProp="name">Services</span>
          </a>
          <a
            href="#how-it-works"
            onClick={(e) => handleNavClick(e, 'how-it-works')}
            className="text-base font-medium text-gray-900 hover:text-blue-900 py-3 border-b border-gray-200 cursor-pointer"
            itemProp="url"
          >
            <span itemProp="name">How It Works</span>
          </a>
          <a
            href="#faq"
            onClick={(e) => handleNavClick(e, 'faq')}
            className="text-base font-medium text-gray-900 hover:text-blue-900 py-3 border-b border-gray-200 cursor-pointer"
            itemProp="url"
          >
            <span itemProp="name">FAQ</span>
          </a>
          <a
            href="#reviews"
            onClick={(e) => handleNavClick(e, 'reviews')}
            className="text-base font-medium text-gray-900 hover:text-blue-900 py-3 border-b border-gray-200 cursor-pointer"
            itemProp="url"
          >
            <span itemProp="name">Reviews</span>
          </a>
          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, 'contact')}
            className="text-base font-medium text-gray-900 hover:text-blue-900 py-3 border-b border-gray-200 cursor-pointer"
            itemProp="url"
          >
            <span itemProp="name">Contact</span>
          </a>
          <a
            href="tel:+16476289141"
            className="bg-blue-900 text-white px-6 py-3 rounded-lg text-base font-semibold hover:bg-blue-800 transition-colors text-center mt-4 cursor-pointer whitespace-nowrap"
            aria-label="Call Passport Visa Photo at 647-628-9141"
          >
            <i className="ri-phone-line mr-2"></i>
            Call Now: (647) 628-9141
          </a>
        </div>
      </div>
    </nav>
  );
}